import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { useScrollDepth } from '../../../hooks/useScrollDepth'
import { track } from '../../../utils/analytics'
import type { ConsentFlowSteps } from '../../../utils/consentFlowHelper'
import type { ConsentLanguage } from '../hooks/useConsentLanguage'
import { CONSENT_IMAGE, ConsentFlowCard, PrimaryAction, StepPill, WorkflowProgress } from './ConsentFlowLayout'

export function ConsentIntroScreen({
  alreadySigned,
  noPendingPrimaryOrders = false,
  workflowSteps,
}: {
  alreadySigned: boolean
  noPendingPrimaryOrders?: boolean
  workflowSteps: ConsentFlowSteps
}) {
  const intl = useIntl()
  const navigate = useNavigate()
  const continueToSecondary = alreadySigned || noPendingPrimaryOrders

  return (
    <ConsentFlowCard
      imageSrc={CONSENT_IMAGE}
      layout="vertical"
      progress={
        <WorkflowProgress current={[workflowSteps.consent]} next={[workflowSteps.secondary, workflowSteps.profile]} />
      }
    >
      <div className="space-y-6">
        <StepPill>
          <FormattedMessage id="consentOptions.consentIntro.step" defaultMessage="Step 1" />
        </StepPill>
        <h1 className="text-3xl font-semibold tracking-tight text-[#002C22] sm:text-4xl">
          <FormattedMessage id="consentOptions.consentIntro.heading" defaultMessage="What is Consent to Testing?" />
        </h1>
        <p className="text-text-body text-base leading-7">
          <FormattedMessage
            id="consentOptions.consentIntro.body"
            defaultMessage="This is your agreement to have whole genome sequencing performed on your or your child's DNA."
          />
        </p>
        <div className="border-border-base bg-background-secondary rounded-xl border px-4 py-3">
          <p className="text-sm leading-6 font-medium text-[#006045]">
            <FormattedMessage
              id="consentOptions.consentIntro.infoBox"
              defaultMessage="Your healthcare provider has ordered Whole Genome Sequencing (WGS), a genetic test that looks for changes in DNA that may be causing health issues. These changes, called variants, can sometimes explain a condition or reveal risks for family members. In some cases, even finding a variant may not lead to a treatment. DNA may also be compared to a family member's to help make sense of the results."
            />
          </p>
        </div>
        <p className="text-text-subtle text-sm leading-6">
          {noPendingPrimaryOrders
            ? intl.formatMessage({
                id: 'consentOptions.consentIntro.noPendingOrders',
                defaultMessage:
                  'There are no orders awaiting Consent to Testing. Next, you can review optional choices.',
              })
            : alreadySigned
              ? intl.formatMessage({
                  id: 'consentOptions.consentIntro.alreadySigned',
                  defaultMessage: 'Your consent is already recorded. Next, you can review optional choices.',
                })
              : intl.formatMessage({
                  id: 'consentOptions.consentIntro.notYetSigned',
                  defaultMessage: "Next, you'll read the consent and give your answer.",
                })}
        </p>
        <PrimaryAction
          onClick={() => {
            track('consent.stage_continue', {
              from: '/consents',
              to: continueToSecondary ? '/consents/secondary' : '/consents/sign',
            })
            navigate(continueToSecondary ? '/consents/secondary' : '/consents/sign')
          }}
        >
          <FormattedMessage id="consentOptions.cta.continue" defaultMessage="Continue" />
        </PrimaryAction>
      </div>
    </ConsentFlowCard>
  )
}

export function ConsentSignScreen({
  checkboxText,
  agreed,
  onAgreedChange,
  confirmed,
  signedAt,
  saving,
  refreshing = false,
  errorMessage,
  onConfirm,
  primaryFlowNotice = false,
  workflowSteps,
  language,
}: {
  checkboxText: string
  agreed: boolean
  onAgreedChange: (value: boolean) => void
  confirmed: boolean
  signedAt: string | null
  saving: boolean
  refreshing?: boolean
  errorMessage: string | null
  onConfirm: () => void
  primaryFlowNotice?: boolean
  workflowSteps: ConsentFlowSteps
  language: ConsentLanguage
}) {
  const intl = useIntl()
  const navigate = useNavigate()
  const trackConsentScroll = useScrollDepth('primary_consent_text')

  return (
    <>
      <div className={confirmed ? undefined : 'pb-44 sm:pb-28'}>
        <ConsentFlowCard
          imageSrc={CONSENT_IMAGE}
          layout="vertical"
          contentAlign="start"
          progress={
            <WorkflowProgress
              current={[workflowSteps.consent]}
              next={[workflowSteps.secondary, workflowSteps.profile]}
            />
          }
        >
          <div className="space-y-6">
            <StepPill>
              <FormattedMessage id="consentOptions.consentSign.step" defaultMessage="Step 1" />
            </StepPill>
            <h1 className="text-3xl font-semibold tracking-tight text-[#002C22] sm:text-4xl">
              <FormattedMessage id="consentOptions.consentSign.heading" defaultMessage="Consent to Testing" />
            </h1>
            {primaryFlowNotice ? (
              <div
                className="border-border-base bg-background-secondary rounded-xl border px-4 py-3 text-sm leading-6 text-[#006045]"
                role="status"
              >
                <FormattedMessage
                  id="consentOptions.consentSign.primaryFlowNotice"
                  defaultMessage="Your clinic saved this order under a flow that completes when each required family member signs consent here. Testing moves forward only after those steps are finished."
                />
              </div>
            ) : null}
            <div ref={trackConsentScroll} className="text-text-body space-y-4 pr-2 text-sm leading-6">
              {language.primaryConsentParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            {confirmed && (
              <div className="space-y-4">
                <p
                  className="border-border-base bg-background-secondary text-text-body rounded-xl border px-4 py-3 text-sm leading-6"
                  role="status"
                >
                  {signedAt
                    ? intl.formatMessage(
                        {
                          id: 'consentOptions.consentSign.signedAt',
                          defaultMessage: 'E-signature recorded on {signedAt}.',
                        },
                        { signedAt }
                      )
                    : intl.formatMessage({
                        id: 'consentOptions.consentSign.alreadyRecorded',
                        defaultMessage: 'Your consent has already been recorded.',
                      })}
                </p>
                <PrimaryAction
                  onClick={() => {
                    track('consent.stage_continue', { from: '/consents/sign', to: '/consents/secondary' })
                    navigate('/consents/secondary')
                  }}
                >
                  <FormattedMessage id="consentOptions.cta.continue" defaultMessage="Continue" />
                </PrimaryAction>
              </div>
            )}
          </div>
        </ConsentFlowCard>
      </div>
      {!confirmed && (
        <div className="border-border-base fixed right-0 bottom-0 left-0 z-20 border-t bg-white shadow-lg shadow-gray-900/10">
          <div className="mx-auto flex max-w-[900px] flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-8">
            <label
              className={`flex cursor-pointer items-start gap-3 rounded-3xl border px-4 py-4 transition ${
                agreed ? 'border-[#006045] bg-white' : 'border-border-base bg-background-primary-soft'
              }`}
            >
              <input
                type="checkbox"
                checked={agreed}
                disabled={refreshing}
                onChange={(event) => onAgreedChange(event.target.checked)}
                className="border-border-base mt-1 h-4 w-4 rounded accent-[#006045]"
              />
              <span className="text-base leading-6 font-semibold text-[#002C22]">{checkboxText}</span>
            </label>
            <div className="flex flex-col items-start gap-2 sm:items-end">
              {errorMessage && (
                <p className="text-error-600 text-sm font-medium" role="alert">
                  {errorMessage}
                </p>
              )}
              <PrimaryAction disabled={!agreed || saving || refreshing} onClick={onConfirm}>
                {saving
                  ? intl.formatMessage({ id: 'consentOptions.consentSign.saving', defaultMessage: 'Saving' })
                  : intl.formatMessage({
                      id: 'consentOptions.consentSign.signAndContinue',
                      defaultMessage: 'Sign & Continue',
                    })}
              </PrimaryAction>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
